/**
 * WhatsApp Floating Button Component
 * Provides direct WhatsApp contact with pre-filled message
 *
 * Features:
 * - Opens WhatsApp with pre-filled message
 * - Animated hover effects
 * - Accessible with ARIA labels
 * - Responsive positioning
 * - i18n support
 */

import { IconButton, Tooltip, Zoom } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const WhatsAppButton = () => {
  const { t } = useTranslation();

  // Configuración de WhatsApp
  const WHATSAPP_NUMBER = "6611301597"; // Número sin espacios ni símbolos
  const defaultMessage = t("whatsapp.defaultMessage");

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(defaultMessage);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Zoom in={true}>
      <Tooltip title={t("whatsapp.tooltip")} placement="left" arrow>
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{
            position: "fixed",
            bottom: "100px", // Arriba del chatbot
            right: "24px",
            zIndex: 1000,
          }}
        >
          <IconButton
            onClick={handleWhatsAppClick}
            aria-label={t("whatsapp.ariaLabel")}
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#25D366", // Color oficial de WhatsApp
              color: "white",
              boxShadow: "0 4px 20px rgba(37, 211, 102, 0.4)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                bgcolor: "#128C7E", // Verde oscuro de WhatsApp
                boxShadow: "0 6px 25px rgba(37, 211, 102, 0.6)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            <WhatsAppIcon fontSize="large" />
          </IconButton>
        </motion.div>
      </Tooltip>
    </Zoom>
  );
};

export default WhatsAppButton;
